import type { UseMutationResult } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';

import type { IApiGetUserResponseBody } from '../../Users';
import type { IApiUpdateOrganizationUserRequestBody } from '../OrganizationsApi.types';
import { useApi } from '~/lib/api/useApi';
import { organizationQueryKeyFactory } from './useOrganizationQuery';
import { organizationsQueryKeyFactory } from './useOrganizationsQuery';
import { organizationUserQueryKeyFactory } from './useOrganizationUserQuery';
import { organizationUsersQueryKeyFactory } from './useOrganizationUsersQuery';

export type IUseOrganizationUpdateUserMutationValues = {
  organizationId: string;
  userId: string;
  body: IApiUpdateOrganizationUserRequestBody;
};

export const useOrganizationUpdateUserMutation = (): UseMutationResult<
  IApiGetUserResponseBody,
  unknown,
  IUseOrganizationUpdateUserMutationValues
> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) =>
      api.organizations.updateUser(
        values.organizationId,
        values.userId,
        values.body,
      ),
    onSuccess: async (_, values) =>
      Promise.all(
        [
          { queryKey: organizationUsersQueryKeyFactory(values) },
          { queryKey: organizationUserQueryKeyFactory(values) },
          { queryKey: organizationQueryKeyFactory(values) },
          { queryKey: organizationsQueryKeyFactory() },
        ].map((filters) => queryClient.invalidateQueries(filters)),
      ),
  });
};
